import { IDataTableV2DateState } from "../../components/TableV2/preDefinedPlugins/DataTableV2Date/DataTableV2Date.types";
import { IDatatableV2AdvancedSearchFilter } from "../../components/TableV2/preDefinedPlugins/SearchFilter/SearchFilter.types";
import { api } from "../../api/api";


import { IQuotesRequest } from "./quote.types";
import { uploadFile } from "../../features/FileUpload/utils";
import { makeApiCall } from "../../helpers/postRequest";
import { endPoints } from "../../constants/endPoints";
import { generateAdvanceFilterQuery } from "../Accounting/AccountingReports/AccountingReportsActions";
import { APIError } from "../../constants/types";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { ERROR_MESSAGES } from "../../constants/enums";
import { IFileUpload } from "../../features/FileUpload/components/FileUpload.type";
import { getUniqueId } from "../../helpers/getUniqueId";

export const fetchQuotesRequestList = createAsyncThunk<
  IQuotesRequest[],
  {
    pageNumber: number;
    rowsInPerPage: number;
    limit: number;
    status: string;
    date: IDataTableV2DateState["dates"];
    searchValue: IDatatableV2AdvancedSearchFilter;
    searchState: { [key: string]: any };
  },
  { rejectValue: APIError }
>(
  "quotes/fetchQuotesRequestList",
  async (
    {
      pageNumber,
      rowsInPerPage,
      limit,
      status,
      date,
      searchValue,
      searchState,
    },
    { rejectWithValue },
  ) => {
    try {
      const query = generateAdvanceFilterQuery(searchState);
      let finalUrl = `${endPoints.quotes.fetchQuotes}?pageNo=${pageNumber}&pageLimit=${limit}&itemPerPage=${rowsInPerPage}&from_date=${date.fromDate}&to_date=${date.toDate}`;
      if (searchValue.length > 0) {
        finalUrl = `${endPoints.quotes.fetchQuotes}?pageNo=${pageNumber}&pageLimit=${limit}&itemPerPage=${rowsInPerPage}&from_date=${
          date.fromDate
        }&to_date=${date.toDate}&advanceFilter=${JSON.stringify(searchValue)}`;
      }
      if (status !== "-1") {
        finalUrl += "&status=" + status;
      }
      if (query) {
        finalUrl += "&" + query;
      }
      const res = await api.get(finalUrl);
      const data: IQuotesRequest[] = res.data.data;
      return data;
    } catch (err: any) {
      return rejectWithValue({
        message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
        status: err.response?.status,
      });
    }
  },
);

export const upsertQuotes = createAsyncThunk<
  IQuotesRequest,
  {
    quote: IQuotesRequest;
    initialQuote: IQuotesRequest;
    isUpdate: boolean;
    file: File | null;
  },
  { rejectValue: APIError }
>(
  "quotes/upsertQuotes",
  async (
    payload: {
      quote: IQuotesRequest;
      initialQuote: IQuotesRequest;
      isUpdate: boolean;
      file: File | null;
    },
    { rejectWithValue },
  ) => {
    const { quote, initialQuote, isUpdate, file } = payload;
    try {
      const asPayload = {
        page_name: quote.type,
      };

      const paths = await uploadFile(
        file,
        "TEST",
        (quote.attachment as string) || "",
        asPayload,
      );

      const res = await makeApiCall(
        {
          url: endPoints.quotes.upsertQuote,
          method: "POST",
          automation: {
            primaryFieldName: "quote_id",
            isUpdate: isUpdate,
          },
        },
        initialQuote,
        {
          ...quote,
          attachment: [paths],
        },
      );

      return res.data.data as IQuotesRequest;
    } catch (err: any) {
      return rejectWithValue({
        message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
        status: err.response?.status,
      });
    }
  },
);

export const addQuoteARSFile = createAsyncThunk<
  { proccessCode: string, filePath: string },
  {
    quote: IQuotesRequest;
    file: File | null;
  },
  { rejectValue: APIError }
>(
  "quotes/addQuoteARSFile",
  async (
    payload: {
      quote: IQuotesRequest;
      file: File | null;
    },
    { rejectWithValue },
  ) => {
    const { quote, file } = payload;
    try {
      const asPayload = {
        page_name: "test",
      };

      const filePath = await uploadFile(file, "TEST", "", asPayload);
     const res = await api.post(endPoints.quotes.addQuoteARSFile, {
      url: filePath,
      customer_policy_id: quote.customer_policy_id,
      customer_id: quote.customer_id,
      type: quote.type,
      sub_type: quote.province,
      inserting_list: quote.inserting_list,
    });
    const proccessCode = res.data.data.process_records_code;
    if (!proccessCode) {
      return rejectWithValue({
        message: "Process records code not found",
        status: 404,
      });
    }
    return { proccessCode: proccessCode, filePath: filePath };
  } catch (err: any) {
    return rejectWithValue({
      message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
      status: err.response?.status,
    });
  }
});

export const addQuoterAfterARSUploaded = createAsyncThunk<
  IQuotesRequest,
  {
    quote: IQuotesRequest;
    initialQuote: IQuotesRequest;
    filePath: string;
  },
  { rejectValue: APIError }
>(
  "quotes/addQuoterAfterARSUploaded",
  async (
    payload: {
      quote: IQuotesRequest;
      initialQuote: IQuotesRequest;
      filePath: string;
    },
    { rejectWithValue },
  ) => {
    const { quote, initialQuote, filePath } = payload;
    try {
      const res = await makeApiCall(
        {
          url: endPoints.quotes.upsertQuote,
          method: "POST",
          automation: {
            primaryFieldName: "quote_id",
            isUpdate: false,
          },
        },
        initialQuote,
        {
          ...quote,
          attachment: [filePath],
        },
      );

      return res.data.data as IQuotesRequest;
    } catch (err: any) {
      return rejectWithValue({
        message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
        status: err.response?.status,
      });
    }
  },
);


export const fetchQuoteReq = createAsyncThunk<
  IQuotesRequest,
  string,
  { rejectValue: APIError }
>(
  "quotes/fetchQuoteReq",
  async (quoteId: string, { dispatch, rejectWithValue }) => {
    try {
      const res = await api.get(
        `${endPoints.quotes.fetchQuotes}?quote_id=${quoteId}`,
      );
      const data = res.data.data;
      if (data.length > 0) {
        const attachments: IFileUpload[] = [];
        for (const item of (data[0].attachment || []) as any) {
          attachments.push({
            file: null,
            key: getUniqueId(),
            path: item,
          });
        }
        return { ...data[0], attachment: attachments };
      } else {
        return rejectWithValue({
          message: "No record found",
          status: 404,
        });
      }
    } catch (err: any) {
      return rejectWithValue({
        message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
        status: err.response?.status,
      });
    }
  },
);
